import type { CandidateProfile, Message, Participant } from '../fixtures/domain.js';

/**
 * How long after an unlock-implying note we keep saying "unlocking…".
 *
 * The number is a judgement about one lane: ajt commits the unlock, publishes
 * to SNS, and the chat backend's SQS consumer projects it onto the row this
 * panel reads. That hop is seconds.
 *
 * Kept close to that rather than padded well past it, because of what the two
 * failure modes actually cost. Too short and the employer gets a retry button
 * that works on the first press — mild. Too long and a backend that is not
 * announcing at all leaves them watching a spinner with nothing to do, which
 * reads as broken and is how this first went wrong in testing.
 *
 * It bounds the claim, not the truth: when it expires the section says it could
 * not load and offers a retry. It never starts asserting the profile is locked,
 * because nothing here ever learned that.
 */
export const UNLOCK_WINDOW_MS = 20_000;

/**
 * How often we re-read `/details` while that window is open.
 *
 * The socket announcement (`data_changed`) is an optimization that can simply
 * not arrive: Stream neither persists nor pushes custom events, so a projection
 * landing while the employer's socket is between reconnects announces to
 * nobody. When it missed, the panel sat on the spinner for the whole window and
 * then offered a "Try again" that worked on the FIRST press — proof the data
 * had been there for seconds and only the nudge was missing.
 *
 * Short enough that the section fills in about as fast as the announcement
 * would have filled it, long enough that a whole window of waiting costs a
 * handful of requests on the one channel the employer is looking at. It runs
 * only while the wait is open, so nothing polls in the background.
 */
export const UNLOCK_POLL_MS = 3_000;

/**
 * The notes that mean an unlock is on its way.
 *
 * Both name an unlock that has ALREADY happened upstream — the candidate
 * accepting the chat request unlocks them on the ajt side, and
 * `employer_viewed_contact` is what ajt fires for the whole unlock, the "View
 * Resume" path included. So the wait these open is never for the unlock itself;
 * it is for the projection to catch up. That is what makes the wait bounded and
 * the pending state honest.
 *
 * `chat_request_declined` is deliberately absent: it is the other answer, and
 * it unlocks nothing.
 */
const UNLOCK_NOTICES = new Set(['chat_request_accepted', 'employer_viewed_contact']);

/** Whether the BFF sent anything the unlock gates. Presence IS the gate — the
 *  server omits all three fields while locked — so this is the same question
 *  the section asks before it renders. */
export function isUnlocked(profile: CandidateProfile): boolean {
  return Boolean(profile.contact?.email || profile.contact?.phone || profile.resume);
}

/** The details read decides; without it, the inbox page's answer. */
export function isLocked(who: Participant, profile = who.profile): boolean {
  return profile?.unlocked !== undefined ? !profile.unlocked : who.locked === true;
}

/** Neither the details read nor the inbox page has answered yet. */
export function isLockUnknown(who: Participant, profile = who.profile): boolean {
  return profile?.unlocked === undefined && who.locked === undefined;
}

/**
 * When the wait opened by the newest unlock note runs out, or `undefined` if no
 * note opened one.
 *
 * **Derived from the thread, never from a flag we set when the event arrived.**
 * That is what makes it survive a reload: the note is a durable message with a
 * server timestamp, so a panel that mounts five seconds into the wait computes
 * the same deadline as the one that watched the note arrive. Nothing is written
 * to storage — which also keeps which candidate an employer is looking at off
 * the disk, the same reason the details route sends `no-store`.
 *
 * Returns the deadline whether or not it has already passed. Deliberately: a
 * deadline in the past and no deadline at all are different situations — one
 * is a promised unlock that never landed, the other is an ordinary locked
 * profile — and collapsing them here would leave the caller unable to tell a
 * failure from a normal state. `Date.now()` is not read at all, so this stays a
 * pure function of the thread.
 *
 * Reads the newest matching note, not the first: an employer whose projection
 * lags again on a later note is waiting on the later one.
 */
export function unlockDeadline(messages: Message[]): number | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m?.system || !m.notice || !UNLOCK_NOTICES.has(m.notice)) continue;
    const at = Date.parse(m.createdAt);
    // An unparseable timestamp cannot be aged, and a note we cannot age must
    // not open a wait at all.
    if (Number.isNaN(at)) return undefined;
    return at + UNLOCK_WINDOW_MS;
  }
  return undefined;
}
